import React, { useRef, useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const InterviewTypeSelector = ({ value, onChange, error, types = [] }) => {
  const scrollRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setShowLeftArrow(scrollLeft > 10);
      setShowRightArrow(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      // Initial check
      checkScroll();
      // Also check on resize
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }
  }, [types]);

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };
  // Map DB types to UI format if needed, or just use them directly if schema matches
  // DB has: name, description
  // UI needs: id (name), label (name), description, icon (default), duration (default/calc), color (default)

  const getIconForType = (type) => {
    const map = {
      'CODING': 'Code',
      'SYSTEM_DESIGN': 'Server',
      'HR': 'Users',
      'RESUME': 'FileText'
    };
    return map[type] || 'Terminal';
  };

  const getColorForType = (type) => {
    const map = {
      'CODING': 'from-violet-500 to-purple-500',
      'SYSTEM_DESIGN': 'from-orange-500 to-red-500',
      'HR': 'from-blue-500 to-cyan-500',
      'RESUME': 'from-amber-500 to-orange-500'
    };
    return map[type] || 'from-gray-500 to-slate-500';
  };

  const interviewTypes = types.map(t => ({
    id: t.name,
    label: t.name.replace(/_/g, ' '), // Prettify name
    description: t.description,
    icon: getIconForType(t.name),
    duration: 'Varies', // You might want to make this dynamic later
    color: getColorForType(t.name)
  }));

  return (
    <div className="space-y-6 relative group/selector">
        <style>
          {`
            .no-scrollbar::-webkit-scrollbar {
              display: none;
            }
            .no-scrollbar {
              -ms-overflow-style: none;
              scrollbar-width: none;
            }
          `}
        </style>

        {/* Scroll Arrows */}
        <button 
          type="button"
          onClick={() => scroll('left')}
          disabled={!showLeftArrow}
          className={`absolute -left-10 top-[40%] -translate-y-1/2 z-20 p-2 text-emerald-600 transition-all duration-300 ${
            showLeftArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-50 pointer-events-none'
          } hover:text-emerald-700 hover:scale-125 active:scale-95`}
        >
          <div className="w-10 h-10 flex items-center justify-center">
            <Icon name="ChevronLeft" size={40} className="stroke-[3]" />
          </div>
        </button>

        <button 
          type="button"
          onClick={() => scroll('right')}
          disabled={!showRightArrow}
          className={`absolute -right-10 top-[40%] -translate-y-1/2 z-20 p-2 text-emerald-600 transition-all duration-300 ${
            showRightArrow ? 'opacity-100 scale-100' : 'opacity-0 scale-50 pointer-events-none'
          } hover:text-emerald-700 hover:scale-125 active:scale-95`}
        >
          <div className="w-10 h-10 flex items-center justify-center">
            <Icon name="ChevronRight" size={40} className="stroke-[3]" />
          </div>
        </button>

        <div 
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto pb-4 no-scrollbar scroll-smooth w-full"
        >
          {interviewTypes?.map((type) => (
            <button
              key={type?.id}
              type="button"
              onClick={() => onChange(type?.id)}
              className={`relative flex-shrink-0 w-[260px] p-5 rounded-2xl border transition-all duration-300 text-left group overflow-hidden ${
                value === type?.id
                  ? 'border-emerald-500 bg-emerald-50/30'
                  : 'border-slate-100 bg-white hover:border-emerald-200'
              }`}
            >
              {/* Subtle background icon for premium feel */}
              <Icon 
                name={type?.icon} 
                size={60} 
                className={`absolute -right-4 -bottom-4 transition-all duration-500 ${
                    value === type?.id ? 'text-emerald-500/10' : 'text-slate-50'
                }`} 
              />

              <div className="relative z-10">
                <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${type?.color} flex items-center justify-center mb-3 shadow-sm`}>
                  <Icon name={type?.icon} size={16} color="#FFFFFF" className="fill-current" />
                </div>

                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-[13px] font-normal text-slate-900">{type?.label==="CODING" ? "DSA" : type?.label}</h4>
                  {value === type?.id && (
                    <div className="w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center">
                      <Icon name="Check" size={10} color="#FFFFFF" className="stroke-[4]" />
                    </div>
                  )}
                </div>
                <p className="text-[11px] leading-relaxed text-slate-500 mb-4 line-clamp-2 pr-8 font-normal">{type?.description}</p>

                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-50 border border-slate-100 rounded text-[9px] font-normal text-slate-400 tracking-widest">
                        <Icon name="Clock" size={10} />
                        <span>{type?.duration}</span>
                    </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      {error && (
        <p className="text-[10px] font-normal text-red-500 flex items-center gap-2 tracking-tight ml-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
};

export default InterviewTypeSelector;
